// Post image resolution — 1:1 port of iOS `mobile/src/lib/xano/postMedia.ts`.
//
// The Profile_Fetch grid endpoints (/standard_posts) return post records
// WITHOUT any media fields — id, content, counts, author only. The image
// for a standard post lives on the full post record (GET /posts/{id}), so
// grid thumbnails resolve lazily through a module-level cache: each post
// is fetched at most once per session, shared across every profile screen.

import { useEffect, useState } from "react";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { resolveMediaUrl } from "@/lib/media";

// Pull an image URL straight off a post record — handles every media shape
// the Xano endpoints emit (array of attachment objects, single object, flat
// url columns, bare strings).
export function extractPostImageUrl(p) {
  if (!p || typeof p !== "object") return "";
  const list = p.media ?? p.media_items ?? p.images ?? p.attachments ?? [];
  const first = Array.isArray(list) ? list[0] : list;
  const fromObj = (m) =>
    m && typeof m === "object"
      ? m.url || m.src || m.file_url || m.image_url || m.path || ""
      : typeof m === "string"
      ? m
      : "";
  // Flat columns — Profile_Fetch /standard_posts now returns image_url on
  // each item; it may be a bare string or a Xano attachment object.
  const raw =
    fromObj(first) ||
    fromObj(p.image_url) ||
    fromObj(p.media_url) ||
    fromObj(p.photo_url) ||
    fromObj(p.image) ||
    "";
  return resolveMediaUrl(raw);
}

// Pull ALL image URLs from a post — used by PostCard so multi-image posts
// render as a 2-column grid.  Order: flat url column first, then any
// media array items (deduped).
export function extractPostImageUrls(p) {
  if (!p || typeof p !== "object") return [];
  const list = p.media ?? p.media_items ?? p.images ?? p.attachments ?? [];
  const arr = Array.isArray(list) ? list : list ? [list] : [];
  const fromObj = (m) =>
    m && typeof m === "object"
      ? m.url || m.src || m.file_url || m.image_url || m.path || ""
      : typeof m === "string"
      ? m
      : "";
  const mediaUrls = arr.map((m) => resolveMediaUrl(fromObj(m))).filter(Boolean);
  const flat = extractPostImageUrl(p);
  if (flat && !mediaUrls.includes(flat)) return [flat, ...mediaUrls];
  return mediaUrls;
}

const cache = new Map(); // post id -> resolved URL (or empty string on miss)
const inflight = new Map(); // post id -> Promise<string>

async function fetchPostImage(postId) {
  const key = String(postId);
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      const res = await api.get(EP.post(postId));
      let full = unwrap(res.data);
      full = full?.post || full;
      let url = extractPostImageUrl(full);
      // Last resort: some records carry only a media_id — resolve via /media/{id}.
      const mediaId = full?.media_id ?? full?.image_media_id;
      if (!url && mediaId) {
        try {
          const mres = await api.get(EP.media(mediaId));
          const m = unwrap(mres.data) || {};
          url = resolveMediaUrl(m.file_url || m.url || m.src || "");
        } catch {
          /* best-effort */
        }
      }
      cache.set(key, url);
      return url;
    } catch {
      cache.set(key, "");
      return "";
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

// Hook — returns the post's image URL, resolving through the cache when
// the grid payload doesn't embed media. `loading` stays true until the
// fetch finishes so tiles can show a shimmer instead of a blank square.
export function usePostImage(post) {
  const inline = extractPostImageUrl(post);
  const pid = post?.id ?? post?.post_id;
  const key = pid != null ? String(pid) : null;
  const cached = key ? cache.get(key) : undefined;
  const [url, setUrl] = useState(inline || cached || "");
  const [loading, setLoading] = useState(!inline && cached === undefined && !!key);

  useEffect(() => {
    if (inline) {
      setUrl(inline);
      setLoading(false);
      return;
    }
    if (!key) {
      setUrl("");
      setLoading(false);
      return;
    }
    const hit = cache.get(key);
    if (hit !== undefined) {
      setUrl(hit);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchPostImage(key).then((u) => {
      if (!cancelled) {
        setUrl(u);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [inline, key]);

  return { url, loading };
}
